import React, { useState, useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale, // Add this for Radar chart
} from "chart.js";
import { Bar, Doughnut, Line, Radar } from "react-chartjs-2";
import "./PagesCss/CreditManagement.css";
import article from "./Content";

// Register ChartJS components including RadialLinearScale for Radar chart
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale // Register the Radar chart scale
);

const CardApprovalOddsChecker = () => {
  const content = article.find((item) => item.id === 3);
  const [user, setUser] = useState({
    creditScore: "720",
    creditHistoryYears: "5",
    openAccounts: "4",
    recentInquiries: "1",
    creditUtilization: "25",
    monthlyIncome: "6000",
    monthlyExpenses: "4500",
    cardType: "Rewards",
    desiredLimit: "5000",
    annualIncome: "72000",
    employmentLength: "3",
    housingPayment: "1500",
    derogatoryMarks: "0",
    bankruptcyHistory: "none",
    cardIssuer: "Chase",
    relationshipWithIssuer: "none"
  });

  const [results, setResults] = useState(null);
  const [showChart, setShowChart] = useState(true);
  const [activeTab, setActiveTab] = useState("calculator");
  const [issuerRequirements, setIssuerRequirements] = useState(null);
  const [alternativeCards, setAlternativeCards] = useState(null);
  const resultsRef = useRef();
  const chartRefs = useRef([]); // Add refs for charts to manage cleanup

  // Cleanup charts on unmount
  useEffect(() => {
    return () => {
      if (chartRefs.current) {
        chartRefs.current.forEach(chart => {
          if (chart && typeof chart.destroy === 'function') {
            chart.destroy();
          }
        });
      }
    };
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    const hasValidData = user.creditScore && user.monthlyIncome;
    if (hasValidData) {
      setTimeout(() => {
        calculateApprovalOdds();
      }, 100);
    }
  }, [user]);

  const handleChange = (field, value) => {
    setUser({ ...user, [field]: value });
  };

  const resetCalculator = () => {
    setUser({
      creditScore: "720",
      creditHistoryYears: "5",
      openAccounts: "4",
      recentInquiries: "1",
      creditUtilization: "25",
      monthlyIncome: "6000",
      monthlyExpenses: "4500",
      cardType: "Rewards",
      desiredLimit: "5000",
      annualIncome: "72000",
      employmentLength: "3",
      housingPayment: "1500",
      derogatoryMarks: "0",
      bankruptcyHistory: "none",
      cardIssuer: "Chase",
      relationshipWithIssuer: "none"
    });
    setResults(null);
    setIssuerRequirements(null);
    setAlternativeCards(null);
  };

  const calculateApprovalOdds = () => {
    let score = parseInt(user.creditScore) || 0;
    let history = parseInt(user.creditHistoryYears) || 0;
    let accounts = parseInt(user.openAccounts) || 0;
    let inquiries = parseInt(user.recentInquiries) || 0;
    let utilization = parseFloat(user.creditUtilization) || 0;
    let income = parseFloat(user.monthlyIncome) || 0;
    let expenses = parseFloat(user.monthlyExpenses) || 0;
    let limit = parseFloat(user.desiredLimit) || 0;
    let employment = parseInt(user.employmentLength) || 0;
    let derogatory = parseInt(user.derogatoryMarks) || 0;
    let bankruptcy = user.bankruptcyHistory;

    let baseOdds = 50;
    const factorScores = {};
    const factorWeights = {
      creditScore: 30,
      creditHistory: 15,
      utilization: 15,
      dtiRatio: 15,
      inquiries: 10,
      accounts: 5,
      employment: 5,
      derogatory: 5
    };

    // Credit Score (30 points)
    if (score >= 800) {
      baseOdds += 30;
      factorScores.creditScore = { score: 30, status: "Excellent", impact: "+30" };
    } else if (score >= 750) {
      baseOdds += 25;
      factorScores.creditScore = { score: 25, status: "Very Good", impact: "+25" };
    } else if (score >= 700) {
      baseOdds += 20;
      factorScores.creditScore = { score: 20, status: "Good", impact: "+20" };
    } else if (score >= 650) {
      baseOdds += 10;
      factorScores.creditScore = { score: 10, status: "Fair", impact: "+10" };
    } else if (score >= 600) {
      baseOdds += 5;
      factorScores.creditScore = { score: 5, status: "Poor", impact: "+5" };
    } else {
      baseOdds -= 10;
      factorScores.creditScore = { score: -10, status: "Very Poor", impact: "-10" };
    }

    // Credit History Length (15 points)
    if (history >= 7) {
      baseOdds += 15;
      factorScores.creditHistory = { score: 15, status: "Excellent", impact: "+15" };
    } else if (history >= 5) {
      baseOdds += 12;
      factorScores.creditHistory = { score: 12, status: "Very Good", impact: "+12" };
    } else if (history >= 3) {
      baseOdds += 8;
      factorScores.creditHistory = { score: 8, status: "Good", impact: "+8" };
    } else if (history >= 1) {
      baseOdds += 5;
      factorScores.creditHistory = { score: 5, status: "Fair", impact: "+5" };
    } else {
      baseOdds -= 5;
      factorScores.creditHistory = { score: -5, status: "Poor", impact: "-5" };
    }

    // Credit Utilization (15 points)
    if (utilization < 10) {
      baseOdds += 15;
      factorScores.utilization = { score: 15, status: "Excellent", impact: "+15" };
    } else if (utilization < 20) {
      baseOdds += 12;
      factorScores.utilization = { score: 12, status: "Very Good", impact: "+12" };
    } else if (utilization <= 30) {
      baseOdds += 8;
      factorScores.utilization = { score: 8, status: "Good", impact: "+8" };
    } else if (utilization <= 50) {
      baseOdds += 3;
      factorScores.utilization = { score: 3, status: "Fair", impact: "+3" };
    } else {
      baseOdds -= 5;
      factorScores.utilization = { score: -5, status: "Poor", impact: "-5" };
    }

    // Debt-to-Income ratio (15 points)
    let dti = expenses && income ? (expenses / income) * 100 : 0;
    if (dti < 20) {
      baseOdds += 15;
      factorScores.dtiRatio = { score: 15, status: "Excellent", impact: "+15" };
    } else if (dti < 30) {
      baseOdds += 12;
      factorScores.dtiRatio = { score: 12, status: "Very Good", impact: "+12" };
    } else if (dti < 40) {
      baseOdds += 8;
      factorScores.dtiRatio = { score: 8, status: "Good", impact: "+8" };
    } else if (dti < 50) {
      baseOdds += 3;
      factorScores.dtiRatio = { score: 3, status: "Fair", impact: "+3" };
    } else {
      baseOdds -= 10;
      factorScores.dtiRatio = { score: -10, status: "Poor", impact: "-10" };
    }

    // Recent Inquiries (10 points)
    if (inquiries === 0) {
      baseOdds += 10;
      factorScores.inquiries = { score: 10, status: "Excellent", impact: "+10" };
    } else if (inquiries === 1) {
      baseOdds += 8;
      factorScores.inquiries = { score: 8, status: "Very Good", impact: "+8" };
    } else if (inquiries <= 2) {
      baseOdds += 5;
      factorScores.inquiries = { score: 5, status: "Good", impact: "+5" };
    } else if (inquiries <= 4) {
      baseOdds += 2;
      factorScores.inquiries = { score: 2, status: "Fair", impact: "+2" };
    } else {
      baseOdds -= 10;
      factorScores.inquiries = { score: -10, status: "Poor", impact: "-10" };
    }

    // Number of Open Accounts (5 points)
    if (accounts >= 3 && accounts <= 7) {
      baseOdds += 5;
      factorScores.accounts = { score: 5, status: "Excellent", impact: "+5" };
    } else if (accounts === 2 || accounts === 8) {
      baseOdds += 3;
      factorScores.accounts = { score: 3, status: "Good", impact: "+3" };
    } else if (accounts === 1 || accounts === 9) {
      baseOdds += 1;
      factorScores.accounts = { score: 1, status: "Fair", impact: "+1" };
    } else if (accounts === 0) {
      baseOdds -= 5;
      factorScores.accounts = { score: -5, status: "Poor", impact: "-5" };
    } else {
      baseOdds -= 8;
      factorScores.accounts = { score: -8, status: "Too Many", impact: "-8" };
    }

    // Employment Length (5 points)
    if (employment >= 5) {
      baseOdds += 5;
      factorScores.employment = { score: 5, status: "Excellent", impact: "+5" };
    } else if (employment >= 3) {
      baseOdds += 4;
      factorScores.employment = { score: 4, status: "Very Good", impact: "+4" };
    } else if (employment >= 2) {
      baseOdds += 3;
      factorScores.employment = { score: 3, status: "Good", impact: "+3" };
    } else if (employment >= 1) {
      baseOdds += 2;
      factorScores.employment = { score: 2, status: "Fair", impact: "+2" };
    } else {
      baseOdds -= 3;
      factorScores.employment = { score: -3, status: "Poor", impact: "-3" };
    }

    // Derogatory Marks (5 points)
    if (derogatory === 0) {
      baseOdds += 5;
      factorScores.derogatory = { score: 5, status: "Excellent", impact: "+5" };
    } else if (derogatory === 1) {
      baseOdds -= 5;
      factorScores.derogatory = { score: -5, status: "Fair", impact: "-5" };
    } else {
      baseOdds -= 15;
      factorScores.derogatory = { score: -15, status: "Poor", impact: "-15" };
    }

    // Bankruptcy History
    if (bankruptcy === "recent") {
      baseOdds -= 25;
    } else if (bankruptcy === "discharged") {
      baseOdds -= 15;
    }

    // Card Type Adjustment
    if (user.cardType === "Premium") baseOdds -= 10;
    else if (user.cardType === "Secured") baseOdds += 15;

    // Desired limit vs income
    if (limit > 0) {
      let ratio = limit / (income * 12);
      if (ratio > 0.5) baseOdds -= 15;
      else if (ratio > 0.3) baseOdds -= 8;
      else if (ratio > 0.1) baseOdds += 5;
    }

    // Relationship with issuer
    if (user.relationshipWithIssuer === "existing") baseOdds += 10;
    else if (user.relationshipWithIssuer === "multiple") baseOdds += 15;

    // Clamp odds between 0 and 100
    baseOdds = Math.max(0, Math.min(100, Math.round(baseOdds)));

    let likelihood = baseOdds >= 80 ? "Very High" : 
                   baseOdds >= 65 ? "High" : 
                   baseOdds >= 45 ? "Medium" : 
                   baseOdds >= 25 ? "Low" : "Very Low";

    let approvalConfidence = baseOdds >= 80 ? "Strongly Recommended" :
                           baseOdds >= 65 ? "Recommended" :
                           baseOdds >= 45 ? "Possible" :
                           baseOdds >= 25 ? "Unlikely" : "Very Unlikely";

    // Generate recommendations
    const recommendations = generateRecommendations(factorScores, baseOdds, user);

    // Calculate estimated credit limit
    const estimatedLimit = calculateEstimatedLimit(user, baseOdds);

    // Get issuer-specific requirements
    const issuerReqs = getIssuerRequirements(user.cardIssuer);
    setIssuerRequirements(issuerReqs);

    // Get alternative card suggestions
    const alternatives = getAlternativeCards(user, baseOdds);
    setAlternativeCards(alternatives);

    setResults({ 
      odds: baseOdds, 
      likelihood, 
      approvalConfidence,
      recommendations, 
      factorScores,
      estimatedLimit: estimatedLimit.toFixed(0),
      factorWeights
    });
  };

  const generateRecommendations = (factors, odds, user) => {
    const recommendations = [];
    
    if (factors.creditScore && factors.creditScore.score < 20) {
      recommendations.push({
        type: "critical",
        text: `Improve credit score from ${user.creditScore} to 700+ for better approval chances`
      });
    }

    if (factors.utilization && factors.utilization.score < 8) {
      recommendations.push({
        type: "warning",
        text: `Reduce credit utilization from ${user.creditUtilization}% to under 30%`
      });
    }

    if (factors.dtiRatio && factors.dtiRatio.score < 8) {
      recommendations.push({
        type: "warning",
        text: "Reduce debt-to-income ratio by paying down existing debts"
      });
    }

    if (factors.inquiries && factors.inquiries.score < 5) {
      recommendations.push({
        type: "info",
        text: `Wait ${6 - user.recentInquiries} months for recent inquiries to age`
      });
    }

    if (factors.accounts && factors.accounts.score < 3) {
      if (user.openAccounts < 3) {
        recommendations.push({
          type: "info",
          text: "Consider opening 1-2 additional credit accounts to build credit history"
        });
      } else {
        recommendations.push({
          type: "warning",
          text: "Avoid opening new accounts until some older accounts are closed"
        });
      }
    }

    if (odds < 65 && user.cardType === "Premium") {
      recommendations.push({
        type: "info",
        text: "Consider applying for a standard or rewards card first to build history with this issuer"
      });
    }

    if (user.relationshipWithIssuer === "none") {
      recommendations.push({
        type: "info",
        text: "Consider opening a basic account with this issuer first to establish a relationship"
      });
    }

    return recommendations;
  };

  const calculateEstimatedLimit = (user, odds) => {
    const baseLimit = parseFloat(user.monthlyIncome) * 3;
    const scoreMultiplier = parseFloat(user.creditScore) / 800;
    const oddsMultiplier = odds / 100;
    
    return baseLimit * scoreMultiplier * oddsMultiplier;
  };

  const getIssuerRequirements = (issuer) => {
    const requirements = {
      Chase: {
        minScore: 680,
        minHistory: "1 year",
        "5/24 Rule": "Approval unlikely if 5+ new accounts in 24 months",
        incomeRequirement: "No specific minimum",
        specialNotes: "Prefers existing banking relationships"
      },
      "American Express": {
        minScore: 670,
        minHistory: "6 months",
        "5/24 Rule": "Does not apply",
        incomeRequirement: "No specific minimum",
        specialNotes: "More flexible with credit limits"
      },
      "Bank of America": {
        minScore: 660,
        minHistory: "1 year",
        "5/24 Rule": "Does not apply",
        incomeRequirement: "$20,000+ annual income",
        specialNotes: "Prefers existing customers"
      },
      Citi: {
        minScore: 670,
        minHistory: "1 year",
        "5/24 Rule": "Does not apply",
        incomeRequirement: "No specific minimum",
        specialNotes: "Good for balance transfers"
      },
      "Capital One": {
        minScore: 580,
        minHistory: "6 months",
        "5/24 Rule": "Does not apply",
        incomeRequirement: "No specific minimum",
        specialNotes: "Good for rebuilding credit"
      },
      Discover: {
        minScore: 600,
        minHistory: "6 months",
        "5/24 Rule": "Does not apply",
        incomeRequirement: "$20,000+ annual income",
        specialNotes: "Good for first credit cards"
      }
    };

    return requirements[issuer] || requirements.Chase;
  };

  const getAlternativeCards = (user, odds) => {
    const alternatives = [];
    
    if (odds < 50) {
      alternatives.push({
        name: "Discover it® Secured",
        issuer: "Discover",
        type: "Secured",
        minScore: 580,
        approvalOdds: "High",
        features: ["Cashback rewards", "Credit building", "No annual fee"]
      });
    }

    if (parseInt(user.creditScore) >= 650 && odds < 70) {
      alternatives.push({
        name: "Chase Freedom Unlimited®",
        issuer: "Chase",
        type: "Rewards",
        minScore: 670,
        approvalOdds: "Medium-High",
        features: ["1.5% cashback", "No annual fee", "$200 bonus"]
      });
    }

    if (parseInt(user.creditScore) >= 700) {
      alternatives.push({
        name: "Citi® Double Cash",
        issuer: "Citi",
        type: "Rewards",
        minScore: 690,
        approvalOdds: "High",
        features: ["2% cashback", "No annual fee", "Balance transfers"]
      });
    }

    return alternatives;
  };

  const printResults = () => {
    window.print();
  };

  const generatePDF = () => {
    alert("PDF generation would be implemented here with a library like jsPDF");
    window.print();
  };

  // Chart data configurations
  const approvalFactorsData = results ? {
    labels: Object.keys(results.factorWeights).map(key => 
      key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
    ),
    datasets: [
      {
        label: 'Maximum Possible Score',
        data: Object.values(results.factorWeights),
        backgroundColor: 'rgba(160, 59, 0, 0.1)',
        borderColor: '#A03B00',
        borderWidth: 2,
        pointBackgroundColor: '#A03B00',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: '#A03B00'
      },
      {
        label: 'Your Score',
        data: Object.keys(results.factorWeights).map(factor => 
          Math.max(0, results.factorScores[factor]?.score || 0)
        ),
        backgroundColor: 'rgba(240, 98, 1, 0.1)',
        borderColor: '#F06201',
        borderWidth: 2,
        pointBackgroundColor: '#F06201',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: '#F06201'
      }
    ],
  } : null;

  const approvalOddsData = results ? {
    labels: ['Your Approval Odds'],
    datasets: [
      {
        label: 'Approval Probability',
        data: [results.odds],
        backgroundColor: results.odds >= 80 ? '#38a169' : 
                       results.odds >= 65 ? '#68d391' :
                       results.odds >= 45 ? '#f6e05e' :
                       results.odds >= 25 ? '#ed8936' : '#e53e3e',
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  } : null;

  const factorImpactData = results ? {
    labels: Object.keys(results.factorScores).map(key => 
      key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
    ),
    datasets: [
      {
        label: 'Impact Score',
        data: Object.values(results.factorScores).map(factor => factor.score),
        backgroundColor: Object.values(results.factorScores).map(factor => 
          factor.score >= 10 ? '#38a169' :
          factor.score >= 5 ? '#68d391' :
          factor.score >= 0 ? '#f6e05e' :
          factor.score >= -5 ? '#ed8936' : '#e53e3e'
        ),
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  } : null;

  // Use Bar chart instead of Radar for better compatibility
  const factorComparisonData = results ? {
    labels: Object.keys(results.factorWeights).map(key => 
      key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
    ),
    datasets: [
      {
        label: 'Maximum Score',
        data: Object.values(results.factorWeights),
        backgroundColor: 'rgba(160, 59, 0, 0.3)',
        borderColor: '#A03B00',
        borderWidth: 1,
      },
      {
        label: 'Your Score',
        data: Object.keys(results.factorWeights).map(factor => 
          Math.max(0, results.factorScores[factor]?.score || 0)
        ),
        backgroundColor: '#F06201',
        borderColor: '#F06201',
        borderWidth: 2,
      }
    ],
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 30
      }
    }
  };

  const comparisonChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 35
      }
    }
  };

  const totalMonthlyDebt = parseFloat(user.monthlyExpenses) || 0;

  return (
    <div className="calculator-container">
      <header className="calculator-header">
        <div className="header-content">
          <h1>Credit Card Approval Odds Checker</h1>
          <p>Calculate your approval chances and get personalized card recommendations</p>
          <div className="debt-summary">
            <div className="summary-item">
              <span>Credit Score:</span>
              <strong>{user.creditScore || 'Not provided'}</strong>
            </div>
            <div className="summary-item">
              <span>Card Type:</span>
              <strong>{user.cardType}</strong>
            </div>
            <div className="summary-item">
              <span>Issuer:</span>
              <strong>{user.cardIssuer}</strong>
            </div>
            {results && (
              <div className="summary-item">
                <span>Approval Confidence:</span>
                <strong className={
                  results.approvalConfidence.includes('Recommended') ? "savings" :
                  results.approvalConfidence.includes('Possible') ? "" : "warning"
                }>
                  {results.approvalConfidence}
                </strong>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Tabs Navigation */}
      <div className="mobile-tabs">
        <button 
          className={`tab-btn ${activeTab === 'calculator' ? 'active' : ''}`}
          onClick={() => setActiveTab('calculator')}
        >
          <i className="fa-solid fa-calculator"></i>
          Calculator
        </button>
        <button 
          className={`tab-btn ${activeTab === 'results' ? 'active' : ''}`}
          onClick={() => setActiveTab('results')}
        >
          <i className="fa-solid fa-chart-pie"></i>
          Results
        </button>
      </div>

      <div className="calculator-layout">
        {/* Left Side - Calculator Inputs */}
        <div className={`left-panel ${activeTab === 'calculator' ? 'active' : ''}`}>
          <div className="cards-section">
            <div className="section-header">
              <h2>Credit Profile & Card Selection</h2>
              <div className="card-actions">
                <button className="btn-reset" onClick={resetCalculator}>
                  Reset All
                </button>
              </div>
            </div>

            <div className="strategy-selector">
              <div className="strategy-info">
                <i className="fa-solid fa-lightbulb"></i>
                {" "}Get personalized approval odds and card recommendations based on your credit profile
              </div>
            </div>

            <div className="input-grid">
              {/* Credit Profile */}
              <div className="input-group">
                <label>Credit Score*</label>
                <input
                  type="number"
                  value={user.creditScore}
                  onChange={(e) => handleChange("creditScore", e.target.value)}
                  placeholder="720"
                  min="300"
                  max="850"
                />
              </div>

              <div className="input-group">
                <label>Credit History (Years)*</label>
                <input
                  type="number"
                  value={user.creditHistoryYears}
                  onChange={(e) => handleChange("creditHistoryYears", e.target.value)}
                  placeholder="5"
                  min="0"
                  max="50"
                />
              </div>

              <div className="input-group">
                <label>Open Credit Accounts</label>
                <input
                  type="number"
                  value={user.openAccounts}
                  onChange={(e) => handleChange("openAccounts", e.target.value)}
                  placeholder="4"
                  min="0"
                  max="20"
                />
              </div>

              <div className="input-group">
                <label>Recent Inquiries (6 months)</label>
                <input
                  type="number"
                  value={user.recentInquiries}
                  onChange={(e) => handleChange("recentInquiries", e.target.value)}
                  placeholder="1"
                  min="0"
                  max="10"
                />
              </div>

              <div className="input-group">
                <label>Credit Utilization (%)</label>
                <input
                  type="number"
                  value={user.creditUtilization}
                  onChange={(e) => handleChange("creditUtilization", e.target.value)}
                  placeholder="25"
                  min="0"
                  max="100"
                />
              </div>

              <div className="input-group">
                <label>Monthly Income ($)*</label>
                <input
                  type="number"
                  value={user.monthlyIncome}
                  onChange={(e) => handleChange("monthlyIncome", e.target.value)}
                  placeholder="6000"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="input-group">
                <label>Monthly Expenses ($)</label>
                <input
                  type="number"
                  value={user.monthlyExpenses}
                  onChange={(e) => handleChange("monthlyExpenses", e.target.value)}
                  placeholder="4500"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="input-group">
                <label>Employment Length (Years)</label>
                <input
                  type="number"
                  value={user.employmentLength}
                  onChange={(e) => handleChange("employmentLength", e.target.value)}
                  placeholder="3"
                  min="0"
                  max="50"
                />
              </div>

              <div className="input-group">
                <label>Derogatory Marks</label>
                <input
                  type="number"
                  value={user.derogatoryMarks}
                  onChange={(e) => handleChange("derogatoryMarks", e.target.value)}
                  placeholder="0"
                  min="0"
                  max="10"
                />
              </div>

              <div className="input-group">
                <label>Bankruptcy History</label>
                <select
                  value={user.bankruptcyHistory}
                  onChange={(e) => handleChange("bankruptcyHistory", e.target.value)}
                >
                  <option value="none">No Bankruptcy</option>
                  <option value="discharged">Discharged (2+ years)</option>
                  <option value="recent">Recent Bankruptcy</option>
                </select>
              </div>

              {/* Card Selection */}
              <div className="input-group">
                <label>Card Issuer*</label>
                <select
                  value={user.cardIssuer}
                  onChange={(e) => handleChange("cardIssuer", e.target.value)}
                >
                  <option value="Chase">Chase</option>
                  <option value="American Express">American Express</option>
                  <option value="Bank of America">Bank of America</option>
                  <option value="Citi">Citi</option>
                  <option value="Capital One">Capital One</option>
                  <option value="Discover">Discover</option>
                </select>
              </div>

              <div className="input-group">
                <label>Card Type*</label>
                <select
                  value={user.cardType}
                  onChange={(e) => handleChange("cardType", e.target.value)}
                >
                  <option value="Standard">Standard Card</option>
                  <option value="Rewards">Rewards Card</option>
                  <option value="Premium">Premium Card</option>
                  <option value="Secured">Secured Card</option>
                  <option value="Business">Business Card</option>
                </select>
              </div>

              <div className="input-group">
                <label>Desired Credit Limit ($)</label>
                <input
                  type="number"
                  value={user.desiredLimit}
                  onChange={(e) => handleChange("desiredLimit", e.target.value)}
                  placeholder="5000"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="input-group">
                <label>Relationship with Issuer</label>
                <select
                  value={user.relationshipWithIssuer}
                  onChange={(e) => handleChange("relationshipWithIssuer", e.target.value)}
                >
                  <option value="none">No Relationship</option>
                  <option value="existing">Existing Customer</option>
                  <option value="multiple">Multiple Accounts</option>
                  <option value="premium">Premium Banking</option>
                </select>
              </div>
            </div>
            <div className="required-note">* Required fields</div>
          </div>

          <div className="actions-section">
            <button className="btn-primary" onClick={calculateApprovalOdds}>
              Check Approval Odds
            </button>
          </div>

          {/* Default tips section */}
          <div className="default-results">
            <h2>Approval Optimization Guide</h2>
            <div className="default-message">
              <p>Enter your credit profile details to calculate approval odds and get personalized card recommendations.</p>
              <div className="default-tips">
                <h3><i className="fa-solid fa-lightbulb"></i> Approval Tips:</h3>
                <ul>
                  <li>Credit scores above 700 significantly improve approval chances</li>
                  <li>Keep credit utilization below 30% for optimal results</li>
                  <li>Limit credit inquiries to 1-2 per 6 months</li>
                  <li>Establish relationships with issuers before applying for premium cards</li>
                  <li>Consider secured cards if you're rebuilding credit</li>
                  <li>Check pre-approval offers before formal applications</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Results */}
        <div className={`right-panel ${activeTab === 'results' ? 'active' : ''}`} ref={resultsRef}>
          {results ? (
            <div className="results-section">
              <div className="section-header">
                <h2>Approval Analysis Results</h2>
                <div className="results-actions">
                  <div className="chart-toggle">
                    <button 
                      className={`toggle-btn ${showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(true)}
                    >
                      <i className="fa-solid fa-chart-column"></i> Charts
                    </button>
                    <button 
                      className={`toggle-btn ${!showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(false)}
                    >
                      <i className="fa-solid fa-clipboard-list"></i> Details
                    </button>
                  </div>
                  <div className="button-group">
                    <button className="btn-secondary" onClick={printResults}>
                      <i className="fa-solid fa-print"></i> Print
                    </button>
                    <button className="btn-secondary" onClick={generatePDF}>
                      <i className="fa-regular fa-file-lines"></i> Save as PDF
                    </button>
                  </div>
                </div>
              </div>

              {/* Results Summary */}
              <div className="results-summary">
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-chart-line"></i></div>
                  <h3>Approval Odds</h3>
                  <div className={`summary-value ${
                    results.odds >= 80 ? "savings" :
                    results.odds >= 65 ? "" :
                    results.odds >= 45 ? "warning" : "warning"
                  }`}>
                    {results.odds}%
                  </div>
                  <div className="summary-note">{results.likelihood} Likelihood</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-thumbs-up"></i></div>
                  <h3>Confidence Level</h3>
                  <div className={`summary-value ${
                    results.approvalConfidence.includes('Recommended') ? "savings" :
                    results.approvalConfidence.includes('Possible') ? "" : "warning"
                  }`}>
                    {results.approvalConfidence}
                  </div>
                  <div className="summary-note">Recommendation</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-credit-card"></i></div>
                  <h3>Estimated Limit</h3>
                  <div className="summary-value">${results.estimatedLimit}</div>
                  <div className="summary-note">Potential credit limit</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-star"></i></div>
                  <h3>Strongest Factor</h3>
                  <div className="summary-value">
                    {Object.entries(results.factorScores).reduce((a, b) => 
                      a[1].score > b[1].score ? a : b
                    )[0].replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </div>
                  <div className="summary-note">Top positive factor</div>
                </div>
              </div>

              {showChart ? (
                <div className="charts-grid">
                  <div className="chart-card">
                    <h3>Approval Probability</h3>
                    {approvalOddsData && <Bar data={approvalOddsData} options={barChartOptions} />}
                    <div className="chart-description">
                      <p><strong>Approval Analysis:</strong> Your estimated probability of being approved for the selected card.</p>
                    </div>
                  </div>
                  
                  <div className="chart-card">
                    <h3>Approval Factor Comparison</h3>
                    {factorComparisonData && <Bar data={factorComparisonData} options={comparisonChartOptions} />}
                    <div className="chart-description">
                      <p><strong>Factor Comparison:</strong> See how your credit factors compare to maximum possible scores.</p>
                    </div>
                  </div>

                  <div className="chart-card">
                    <h3>Factor Impact Scores</h3>
                    {factorImpactData && <Bar data={factorImpactData} options={barChartOptions} />}
                    <div className="chart-description">
                      <p><strong>Impact Analysis:</strong> How each factor contributes to your overall approval odds.</p>
                    </div>
                  </div>

                  {alternativeCards && alternativeCards.length > 0 && (
                    <div className="chart-card">
                      <h3>Alternative Card Options</h3>
                      <div className="alternatives-list">
                        {alternativeCards.map((card, index) => (
                          <div key={index} className="alternative-card">
                            <h4>{card.name}</h4>
                            <div className="alternative-details">
                              <p><strong>Issuer:</strong> {card.issuer}</p>
                              <p><strong>Type:</strong> {card.type}</p>
                              <p><strong>Min Score:</strong> {card.minScore}</p>
                              <p><strong>Approval Odds:</strong> 
                                <span className={card.approvalOdds.includes('High') ? "savings" : ""}>
                                  {card.approvalOdds}
                                </span>
                              </p>
                              <p><strong>Features:</strong> {card.features.join(', ')}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="results-details">
                  <div className="results-grid">
                    <div className="result-card">
                      <h3>Approval Analysis</h3>
                      <div className="result-stats">
                        <div className="stat">
                          <label>Overall Approval Odds</label>
                          <span className={`stat-value ${
                            results.odds >= 80 ? "savings" :
                            results.odds >= 65 ? "" :
                            results.odds >= 45 ? "warning" : "warning"
                          }`}>
                            {results.odds}% - {results.likelihood}
                          </span>
                        </div>
                        <div className="stat">
                          <label>Confidence Level</label>
                          <span className={`stat-value ${
                            results.approvalConfidence.includes('Recommended') ? "savings" :
                            results.approvalConfidence.includes('Possible') ? "" : "warning"
                          }`}>
                            {results.approvalConfidence}
                          </span>
                        </div>
                        <div className="stat">
                          <label>Estimated Credit Limit</label>
                          <span className="stat-value">${results.estimatedLimit}</span>
                        </div>
                        <div className="stat">
                          <label>Selected Card</label>
                          <span className="stat-value">{user.cardType} Card - {user.cardIssuer}</span>
                        </div>
                      </div>
                    </div>

                    {/* Factor Breakdown */}
                    <div className="result-card">
                      <h3>Approval Factor Breakdown</h3>
                      <div className="breakdown-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Factor</th>
                              <th>Your Status</th>
                              <th>Impact</th>
                              <th>Score</th>
                              <th>Weight</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(results.factorScores).map(([factor, data]) => (
                              <tr key={factor}>
                                <td>{factor.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</td>
                                <td>{data.status}</td>
                                <td className={data.score >= 5 ? "savings" : data.score < 0 ? "warning" : ""}>
                                  {data.impact}
                                </td>
                                <td>{data.score}</td>
                                <td>{results.factorWeights[factor]}</td>
                              </tr>
                            ))}
                            <tr>
                              <td colSpan="3"><strong>Total Score</strong></td>
                              <td colSpan="2"><strong>{results.odds}%</strong></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Issuer Requirements */}
                  {issuerRequirements && (
                    <div className="scenario-comparison">
                      <h3>{user.cardIssuer} Card Requirements</h3>
                      <div className="scenario-grid">
                        <div className="scenario-card">
                          <h4>Minimum Requirements</h4>
                          <div className="scenario-stats">
                            <div className="stat">
                              <label>Credit Score</label>
                              <span className={parseInt(user.creditScore) >= issuerRequirements.minScore ? "savings" : "warning"}>
                                {issuerRequirements.minScore}+ (Yours: {user.creditScore})
                              </span>
                            </div>
                            <div className="stat">
                              <label>Credit History</label>
                              <span>{issuerRequirements.minHistory}</span>
                            </div>
                            <div className="stat">
                              <label>Income Requirement</label>
                              <span>{issuerRequirements.incomeRequirement}</span>
                            </div>
                          </div>
                        </div>

                        <div className="scenario-card">
                          <h4>Special Rules</h4>
                          <div className="scenario-stats">
                            <div className="stat">
                              <label>5/24 Rule</label>
                              <span>{issuerRequirements["5/24 Rule"]}</span>
                            </div>
                            <div className="stat">
                              <label>Relationship</label>
                              <span>{issuerRequirements.specialNotes}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Alternative Cards */}
                  {alternativeCards && alternativeCards.length > 0 && (
                    <div className="scenario-comparison">
                      <h3>Recommended Alternative Cards</h3>
                      <div className="scenario-grid">
                        {alternativeCards.map((card, index) => (
                          <div key={index} className="scenario-card">
                            <h4>{card.name}</h4>
                            <div className="scenario-stats">
                              <div className="stat">
                                <label>Issuer</label>
                                <span>{card.issuer}</span>
                              </div>
                              <div className="stat">
                                <label>Card Type</label>
                                <span>{card.type}</span>
                              </div>
                              <div className="stat">
                                <label>Minimum Score</label>
                                <span>{card.minScore}</span>
                              </div>
                              <div className="stat">
                                <label>Approval Odds</label>
                                <span className={card.approvalOdds.includes('High') ? "savings" : ""}>
                                  {card.approvalOdds}
                                </span>
                              </div>
                              <div className="stat">
                                <label>Key Features</label>
                                <span>{card.features.join(', ')}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actionable Recommendations */}
                  <div className="default-results">
                    <h3>Action Plan</h3>
                    <div className="recommendations-list">
                      {results.recommendations.map((rec, i) => (
                        <div key={i} className={`default-tips ${rec.type}`}>
                          <i className={`fa-solid ${
                            rec.type === 'critical' ? 'fa-exclamation-triangle' :
                            rec.type === 'warning' ? 'fa-exclamation-circle' :
                            rec.type === 'success' ? 'fa-check-circle' : 'fa-info-circle'
                          }`}></i>
                          <span>{rec.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="no-results">
              <h3>No results yet</h3>
              <p>Enter your credit profile details to check your card approval odds and get personalized recommendations.</p>
            </div>
          )}
        </div>
      </div>
      {content && content.content}
    </div>
  );
};

export default CardApprovalOddsChecker;